import {inject, provider} from "react-ioc";
import React, {Component} from "react";
import { InboxStore } from "./inbox.store";
import {
    BoxCenter,
    FullCenteredContainerCenter
} from "app/modules/common/layout/components/containers";
import {InputBase, Button, Box, Typography, Divider, Stack, Chip, IconButton, Tooltip, Paper, TextField} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { SearchPaper, SearchVerDivider } from "app/modules/common/layout/components/search";
import {Loading} from "app/modules/common/layout/components/loading";
import {observer} from "mobx-react";
import { EmailMessageListItem, EmailMessageThreadItem, EmailContact, EmailAttachmentListItem } from "../../api/clients.api";
import { Editor } from "@tinymce/tinymce-react";
import MailIcon from '@mui/icons-material/Mail';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { email } from "app/modules/common/form/validators";
import { PusherTenantService } from "app/modules/common/pusher/PusherTenantService";
import {useParams} from "react-router-dom";

interface EmailMessageBoxProps {
    email: EmailMessageListItem | EmailMessageThreadItem;
    isSelected: boolean;
    isEven: boolean;
    onSelect: () => void;
    showThreadIcon?: boolean;
    showBotIcon?: boolean;
}

// TinyMCE so the global var exists
// eslint-disable-next-line no-unused-vars
// @ts-ignore
import tinymce from 'tinymce/tinymce';
import { NavigationStore } from "app/modules/company/stores/NavigationStore";
import {FeatureFlagsService} from "app/stores/FeatureFlagsService";

const LeftFilterPanel = (props) => <Box sx={{position: "absolute", width: 350, height: "120px", borderRadius: "0px", paddingTop: "10px", paddingBottom: "10px", display: props.isHidden && "none"}}>{props.children}</Box>;
const LeftPanel = (props) => <Paper sx={{position: "absolute", width: 350, height: "calc(100vh - 125px)", top: "125px", borderRadius: "0px", overflowY: "auto", display: props.isHidden && "none"}}>{props.children}</Paper>;
const SubLeftPanel = (props) => <Paper sx={{position: "absolute", left: 550, width: 350, height: "calc(100vh - 125px)", top: "125px", borderRadius: "0px", overflowY: "auto", display: props.isHidden && "none"}}>{props.children}</Paper>;
const LeftContentPanel = (props) => <Box sx={{marginLeft: props.hideLeftPanel ? "0" : 350 + (props.messageWithThread ? 350 : 0) + "px", height: "calc(100vh - 64px)", overflow: "auto"}}>{props.children}</Box>;

const EmailMessageBox: React.FC<EmailMessageBoxProps> = observer(({ email, isSelected, isEven, onSelect, showThreadIcon = false, showBotIcon = false }) => {
    const handleClick = () => {
        onSelect();
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                p: 1,
                cursor: 'pointer',
                backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.16)' :
                               email.isSent ? 'rgba(76, 175, 80, 0.16)' :
                               isEven ? 'rgba(0, 0, 0, 0.06)' : 'transparent',
                '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)'
                }
            }}
        >
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box>
                    <Typography variant="body2" component="div">
                        {email.isSent ? "You" : email.from[0]?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                        ({email.from[0]?.address})
                    </Typography>
                </Box>
                <Box sx={{display: 'flex', gap: 1}}>
                    {!email.isRead && (
                        <Box component="span" sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'error.main',
                            display: 'inline-block'
                        }} />
                    )}
                    {showBotIcon && email.isExtractionProcessed && (
                        <Box component="span" sx={{color: 'text.secondary'}}>
                            <SmartToyIcon fontSize="small" color={email.isExtractionSuccessful ? "primary" : "error"} />
                        </Box>
                    )}
                    {email.attachments?.length > 0 && (
                        <Box component="span" sx={{color: 'text.secondary'}}>
                            📎
                        </Box>
                    )}
                    {showThreadIcon && email.messagesCount > 1 && (
                        <Box component="span" sx={{color: 'text.secondary'}}>
                            💬
                        </Box>
                    )}
                </Box>
            </Box>
            <Divider sx={{mb: 1}}/>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="body2" color="text.secondary" noWrap sx={{flex: 1}}>
                    {email.subject}
                </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', textAlign: 'right'}}>
                {showThreadIcon ? email.sortDate?.fromNow() : email.receivedDate?.fromNow()}
            </Typography>
        </Box>
    );
});

interface EditorEmailProps {
    inboxStore: InboxStore
}

const EditorEmail: React.FC<EditorEmailProps> = observer(({ inboxStore  }) => {
    return <Editor
        ref={inboxStore.editorRef}
        onInit={(_, editor) => {
            (inboxStore.editorRef as any).current = editor;
        }}
        licenseKey="gpl"
        value={inboxStore.replyForm.$.content.value}
        onEditorChange={(content) => inboxStore.replyForm.$.content.onChange(content)}
        init={{
            height: 300,
            menubar: false,
            plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
            ],
            toolbar: 'undo redo | blocks | ' +
                'bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat'
        }}
    />
})

@provider(InboxStore)
@observer
class Index extends Component<any, any> {
    @inject inboxStore: InboxStore;
    @inject pusherTenantService: PusherTenantService;
    @inject navigationStore: NavigationStore;
    @inject featureFlagsService: FeatureFlagsService;

    validateEmail(emailAddress: string): boolean {
        return !email(emailAddress);
    }

    handleEmailInput(e: React.KeyboardEvent, input: string, addEmail: (email: string) => void, setInput: (input: string) => void) {
        if (e.key === 'Enter' && input) {
            const trimmedEmail = input.trim();
            if (this.validateEmail(trimmedEmail)) {
                addEmail(trimmedEmail);
                setInput('');
            } else {
                // You might want to show an error message here
                console.warn('Invalid email format');
            }
        }
    }

    renderEmailContactChip(emailContact: EmailContact, index: number) {
        return <Chip key={index} label={emailContact.name + (!emailContact.name?.length? "" : " (") + emailContact.address + (!emailContact.name?.length? "" : ")")} />;
    }

    render() {
        return (
            <React.Fragment>
                {this.inboxStore.isInitLoading ? <Loading/> :
                    this.inboxStore.noIntegrations ? <FullCenteredContainerCenter>
                            <BoxCenter>
                                <Box sx={{paddingTop: "20vh"}}>
                                    <Typography variant="h6">Please add an integration to start (Settings &#x2192; Integrations &#x2192; Create  &#x2192; Inbox).</Typography>
                                </Box>
                            </BoxCenter>
                        </FullCenteredContainerCenter> :
                        <React.Fragment>
                        <LeftFilterPanel isHidden={this.inboxStore.isLeftPanelHidden}>
                            <BoxCenter>
                                <SearchPaper>
                                    <IconButton
                                        onClick={() => this.inboxStore.toggleUnreadFilter()}
                                        color={this.inboxStore.filters.$.showUnreadOnly.value ? "primary" : "default"}
                                        size="small"
                                    >
                                        <Tooltip title={this.inboxStore.filters.$.showUnreadOnly.value ? "Show All Messages" : "Show Unread Only"}>
                                            <MailIcon />
                                        </Tooltip>
                                    </IconButton>
                                    <InputBase
                                        sx={{ ml: 1, flex: 1 }}
                                        value={this.inboxStore.filters.$.search.value}
                                        onChange={(e)=>this.inboxStore.filters.$.search.onChange(e.target.value)}
                                        placeholder="Search"
                                        inputProps={{ 'aria-label': 'search' }}
                                    />
                                    <SearchVerDivider />
                                    <Button variant="text" onClick={() => this.inboxStore.applySearchFilter()}>Search</Button>
                                </SearchPaper>
                            </BoxCenter>
                        </LeftFilterPanel>
                        <LeftPanel isHidden={this.inboxStore.isLeftPanelHidden}>
                            <Box>
                                {this.inboxStore.items?.map((email: EmailMessageListItem, index: number) => (
                                    <React.Fragment key={email.id}>
                                        <EmailMessageBox
                                            email={email}
                                            isSelected={this.inboxStore.selectedMessage?.id === email.id}
                                            isEven={index % 2 === 1}
                                            onSelect={() => {
                                                this.inboxStore.selectMessage(email);
                                            }}
                                            showThreadIcon={true}
                                            showBotIcon={true}
                                        />
                                    </React.Fragment>
                                ))}
                            </Box>
                            <Box sx={{p: 2, display: 'flex', justifyContent: 'center'}}>
                                <Button
                                    disabled={this.inboxStore.filters.$.offset.value === 0}
                                    onClick={() => { this.inboxStore.goToPreviousPage(); }}>
                                    Previous
                                </Button>
                                <Button
                                    disabled={this.inboxStore.filters.$.offset.value + this.inboxStore.filters.$.pageSize.value >= this.inboxStore.totalCount}
                                    onClick={() => { this.inboxStore.goToNextPage(); }}>
                                    Next
                                </Button>
                            </Box>
                        </LeftPanel>
                        <SubLeftPanel isHidden={(this.inboxStore.emailThread?.length ?? 0) == 0}>
                            <Box>
                                {this.inboxStore.emailThread?.map((email: EmailMessageThreadItem, index: number) => (
                                    <React.Fragment key={email.id}>
                                        <EmailMessageBox
                                            email={email}
                                            isSelected={this.inboxStore.selectedMessageThread?.id === email.id}
                                            isEven={index % 2 === 1}
                                            onSelect={() => {
                                                this.inboxStore.selectMessageThread(email);
                                            }}
                                        />
                                    </React.Fragment>
                                ))}
                            </Box>
                        </SubLeftPanel>
                        <LeftContentPanel hideLeftPanel={this.inboxStore.isLeftPanelHidden} messageWithThread={this.inboxStore.emailThread?.length >= 1}>
                            { this.inboxStore.emailThread?.length && this.inboxStore.selectedMessageThread ? (this.inboxStore.isMessageLoading ? <Loading/> :
                                <Box sx={{p: 2, backgroundColor: "white", height: "calc(100vh - 96px)", display: "flex", flexDirection: "column"}}>
                                    <Box>
                                        <Stack direction="row" spacing={2}>
                                            {this.featureFlagsService.isEnabled("email-parser-manual") && <Tooltip title="Extract data using AI">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.inboxStore.openAIExtractionDialog(this.inboxStore.selectedMessageThread.id, this.inboxStore.selectedMessageThread.integrationId)}
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            color: 'primary.dark'
                                                        }
                                                    }}
                                                >
                                                    <SmartToyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>}
                                            <Box>
                                                <Typography variant="h6">{this.inboxStore.selectedMessageThread?.subject}</Typography>
                                            </Box>
                                        </Stack>
                                        <Divider sx={{my: 1}}/>
                                        <Stack direction="row" spacing={2}>
                                            <BoxCenter><Typography variant="subtitle2">From:</Typography></BoxCenter>
                                            {this.inboxStore.selectedMessageThread.from?.map((emailContact: EmailContact, index: number) => {
                                                return this.renderEmailContactChip(emailContact, index);
                                            })}
                                        </Stack>
                                        <Stack direction="row" spacing={2} sx={{mt: 1}}>
                                            <BoxCenter><Typography variant="subtitle2">To:</Typography></BoxCenter>
                                            {this.inboxStore.selectedMessageThread.to?.map((emailContact: EmailContact, index: number) => {
                                                return this.renderEmailContactChip(emailContact, index);
                                            })}
                                        </Stack>
                                        {this.inboxStore.selectedMessageThread.cc && this.inboxStore.selectedMessageThread.cc.length > 0 && <Stack direction="row" spacing={2} sx={{mt: 1}}>
                                            <BoxCenter><Typography variant="subtitle2">CC:</Typography></BoxCenter>
                                            {this.inboxStore.selectedMessageThread.cc?.map((emailContact: EmailContact, index: number) => {
                                                return this.renderEmailContactChip(emailContact, index);
                                            })}
                                        </Stack>}
                                        <Divider sx={{my: 1}}/>
                                        {this.inboxStore.selectedMessageThread.attachments && this.inboxStore.selectedMessageThread.attachments.length > 0 && <React.Fragment>
                                            <Stack direction="row" spacing={2} sx={{mt: 1}}>
                                                <BoxCenter><Typography variant="subtitle2">Attachments:</Typography></BoxCenter>
                                                {this.inboxStore.selectedMessageThread.attachments?.map((attachment: EmailAttachmentListItem, index: number) => {
                                                    return (
                                                        <Box key={index} component="a" href={attachment.filePath} target="_blank" sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            textDecoration: 'none',
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                textDecoration: 'underline'
                                                            }
                                                        }}>
                                                            <Box component="span" sx={{mr: 0.5}}>📎</Box>
                                                            {attachment.fileName}
                                                        </Box>
                                                    )
                                                })}
                                            </Stack>
                                            <Divider sx={{my: 1}}/>
                                        </React.Fragment>}
                                    </Box>
                                    <Box sx={{flex: 1, overflow: "auto"}}>
                                        <iframe
                                            srcDoc={this.inboxStore.selectedMessageThread.body}
                                            style={{
                                                width: '100%',
                                                height: '95%',
                                                border: 'none',
                                                backgroundColor: 'white'
                                            }}
                                            sandbox="allow-same-origin"
                                            title="Email content"
                                        />
                                    </Box>
                                    {!this.inboxStore.selectedMessageThread.isSent && (
                                        <Box sx={{mt: 2}}>
                                            <Divider sx={{my: 1}}/>
                                            <Stack direction="column" spacing={2} sx={{mt: 1}}>
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                    <BoxCenter><Typography variant="subtitle2">To:</Typography></BoxCenter>
                                                    <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 1}}>
                                                        {this.inboxStore.replyToEmails.length > 0 && <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', gap: 1}}>
                                                            {this.inboxStore.replyToEmails.map((email, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={email}
                                                                    onDelete={() => {
                                                                        const newEmails = [...this.inboxStore.replyToEmails];
                                                                        newEmails.splice(index, 1);
                                                                        this.inboxStore.setReplyToEmails(newEmails);
                                                                    }}
                                                                    size="small"
                                                                />
                                                            ))}
                                                        </Stack>}
                                                        <TextField
                                                            variant="standard"
                                                            size="small"
                                                            placeholder="To: email address"
                                                            value={this.inboxStore.replyToInput}
                                                            onChange={(e) => this.inboxStore.setReplyToInput(e.target.value)}
                                                            onKeyDown={(e) => this.handleEmailInput(
                                                                e,
                                                                this.inboxStore.replyToInput,
                                                                this.inboxStore.addReplyToEmail,
                                                                this.inboxStore.setReplyToInput
                                                            )}
                                                            sx={{flex: 1, minWidth: 100}}
                                                        />
                                                    </Box>
                                                </Stack>
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                    <BoxCenter><Typography variant="subtitle2">CC:</Typography></BoxCenter>
                                                    <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 1}}>
                                                        {this.inboxStore.replyCcEmails.length > 0 && <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', gap: 1}}>
                                                            {this.inboxStore.replyCcEmails.map((email, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={email}
                                                                    onDelete={() => {
                                                                        const newEmails = [...this.inboxStore.replyCcEmails];
                                                                        newEmails.splice(index, 1);
                                                                        this.inboxStore.setReplyCcEmails(newEmails);
                                                                    }}
                                                                    size="small"
                                                                />
                                                            ))}
                                                        </Stack>}
                                                        <TextField
                                                            variant="standard"
                                                            size="small"
                                                            placeholder="CC: email address"
                                                            value={this.inboxStore.replyCcInput}
                                                            onChange={(e) => this.inboxStore.setReplyCcInput(e.target.value)}
                                                            onKeyDown={(e) => this.handleEmailInput(
                                                                e,
                                                                this.inboxStore.replyCcInput,
                                                                this.inboxStore.addReplyCcEmail,
                                                                this.inboxStore.setReplyCcInput
                                                            )}
                                                            sx={{flex: 1, minWidth: 100}}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </Stack>
                                            <Divider sx={{my: 1}}/>
                                            <EditorEmail inboxStore={this.inboxStore} key={this.inboxStore.selectedMessageThread.id} />
                                            <Box sx={{mt: 2, mb: 2}}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        component="label"
                                                        startIcon={<Box component="span">📎</Box>}
                                                    >
                                                        Attach File
                                                        <input
                                                            type="file"
                                                            hidden
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) {
                                                                    this.inboxStore.addAttachment(file);
                                                                }
                                                            }}
                                                        />
                                                    </Button>
                                                    {this.inboxStore.attachments.length > 0 && (
                                                        <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', gap: 1}}>
                                                            {this.inboxStore.attachments.map((file, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={file.name}
                                                                    onDelete={() => this.inboxStore.removeAttachment(index)}
                                                                    size="small"
                                                                />
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </Box>
                                            <Divider sx={{my: 1}}/>
                                            <Box sx={{mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between'}}>
                                                <LoadingButton
                                                    variant="contained"
                                                    onClick={() => this.inboxStore.sendReply()}
                                                    disabled={!this.inboxStore.replyForm.$.content.value || this.inboxStore.isReplyLoading}
                                                    loading={this.inboxStore.isReplyLoading}
                                                >
                                                    Reply
                                                </LoadingButton>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>) : <BoxCenter>
                                <Box sx={{paddingTop: "20vh"}}>
                                    <Typography variant="h6">No messages is selected.</Typography>
                                </Box>
                            </BoxCenter>
                            }
                        </LeftContentPanel>
                    </React.Fragment>
                }
            </React.Fragment>
        );
    }

    async componentDidMount() {
        const { id } = this.props.urlParams;
        await this.inboxStore.Init(id);
        this.pusherTenantService.bindToTenantChannel("InboxNewMessages", this.inboxNewMessagesHandler);
        this.pusherTenantService.bindToTenantChannel("InboxSentMessages", this.inboxNewMessagesHandler);
    }

    inboxNewMessagesHandler = async (data: { MessageCount: number, IntegrationId: number }) => {
        if(data.MessageCount > 0 && data.IntegrationId == this.inboxStore.filters.$.integrationId.value) {
            await this.inboxStore.updateNewMessages();
            this.navigationStore.setActiveInboxActions(this.inboxStore.filters.$.integrationId.value, true);
        }
    }

    async componentWillUnmount() {
        this.pusherTenantService.unbindToTenantChannel("InboxNewMessages", this.inboxNewMessagesHandler);
        this.pusherTenantService.unbindToTenantChannel("InboxSentMessages", this.inboxNewMessagesHandler);

        this.navigationStore.setActiveInboxActions(this.inboxStore.filters.$.integrationId.value, false);
    }
}

export default (props) => {
    const urlParams = useParams();
    const { id } = urlParams;
    return <Index {...props} urlParams={urlParams} key={id} />;
}

